import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, onUnmounted, ref } from 'vue';
import type { LeagueSize } from '@/interface/enum';
import { SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { useOddsToken } from '@/libraries/oddsApi/composables/useOddsToken';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useSelectionSubscription, useSelectionV2Subscription } from '@/types';

export function useSelectionSubscriptionCallback({
    marketId,
    selectionId,
    sportType,
    leagueSize,
    leagueId,
    isActive = ref(true),
}: {
    marketId: Ref<number>;
    selectionId: Ref<number>;
    sportType: SportType;
    leagueSize: LeagueSize;
    leagueId: number;
    isActive?: Ref<boolean>;
}) {
    const { isDynamicMarketMTokenEnabled } = storeToRefs(useFeatureToggleStore());
    const { getOddsToken } = useOddsToken();

    const oldVariables = deepCompareComputed(() => ({
        query: {
            marketId: marketId.value,
            selectionId: selectionId.value,
        },
    }));

    const oldEnabled = computed(() => isActive.value && !isDynamicMarketMTokenEnabled.value);

    const oldSubscription = useSelectionSubscription(
        oldVariables,
        deepCompareComputed(() => ({
            enabled: oldEnabled.value,
        })),
    );

    const variables = deepCompareComputed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize);

        return {
            query: {
                marketId: marketId.value,
                selectionId: selectionId.value,
                oddsToken,
                sportType: SportType.toOddsApi(sportType),
                leagueId,
            },
        };
    });

    const enabled = computed(() => isActive.value && isDynamicMarketMTokenEnabled.value && !!variables.value.query.oddsToken);

    const subscription = useSelectionV2Subscription(
        variables,
        deepCompareComputed(() => ({
            enabled: enabled.value,
        })),
    );

    type UpdatedFn = () => void;
    let updatedFn: UpdatedFn[] = [];
    const onUpdated = (callback: UpdatedFn) => {
        updatedFn.push(callback);
        onUnmounted(() => {
            updatedFn = updatedFn.filter(fn => fn !== callback);
        });
    };

    type DeletedFn = () => void;
    let deletedFn: DeletedFn[] = [];
    const onDeleted = (callback: DeletedFn) => {
        deletedFn.push(callback);
        onUnmounted(() => {
            deletedFn = deletedFn.filter(fn => fn !== callback);
        });
    };

    oldSubscription.onSubscription((payload) => {
        if (payload === undefined) return;
        if (payload === null) {
            deletedFn.forEach(fn => fn());
            return;
        }

        updatedFn.forEach(fn => fn());
    });
    subscription.onSubscription((payload) => {
        if (payload === undefined) return;
        if (payload === null) {
            deletedFn.forEach(fn => fn());
            return;
        }

        updatedFn.forEach(fn => fn());
    });

    return {
        onUpdated,
        onDeleted,
    };
}
